import type { Timestamp } from "@firebase/firestore-types";
import { z } from "zod";
import { accountFullPath } from "../account/index.js";
import type { CategoryId } from "../categories.js";
import type { TrackedCategorySplit } from "../trackedCategory/index.js";
import type {
  TransactionFile,
  TransferRequestDecision,
  VerificationOfPayee,
} from "../transaction/index.js";

// Treezor `*Date` strings are `YYYY-MM-DD HH:MM:SS` in Europe/Paris local time, with no offset.

export const treezorBeneficiariesPathFromAccount = "treezorBeneficiaries";
export const treezorBeneficiariesFullPath = (accountId: string): string =>
  `${accountFullPath(accountId)}/${treezorBeneficiariesPathFromAccount}`;
export const treezorBeneficiaryFullPath = (accountId: string, beneficiaryId: string): string =>
  `${treezorBeneficiariesFullPath(accountId)}/${beneficiaryId}`;

export const treezorOperationsPathFromAccount = "treezorOperations";
export const treezorOperationsFullPath = (accountId: string): string =>
  `${accountFullPath(accountId)}/${treezorOperationsPathFromAccount}`;
export const treezorOperationFullPath = (accountId: string, operationId: string): string =>
  `${treezorOperationsFullPath(accountId)}/${operationId}`;

export const treezorTransferRequestsPathFromAccount = "treezorTransferRequests";
export const treezorTransferRequestsFullPath = (accountId: string): string =>
  `${accountFullPath(accountId)}/${treezorTransferRequestsPathFromAccount}`;
export const treezorTransferRequestFullPath = (
  accountId: string,
  transferRequestId: string,
): string => `${treezorTransferRequestsFullPath(accountId)}/${transferRequestId}`;

export const UserTypeId = {
  Physical: 1,
  Business: 2,
  NonGovernmentalOrganization: 3,
  GovernmentalOrganization: 4,
} as const;
export type UserTypeId = (typeof UserTypeId)[keyof typeof UserTypeId];

export const RelationshipTypeId = {
  LegalRepresentative: 1,
  EffectiveBeneficiaryShares: 2,
  CompanyRepresentative: 3,
  Employee: 4,
} as const;
export type RelationshipTypeId = (typeof RelationshipTypeId)[keyof typeof RelationshipTypeId];

export const WalletTypeId = {
  ElectronicMoney: 9,
  PaymentAccountSepa: 10,
  Master: 15,
} as const;
export type WalletTypeId = (typeof WalletTypeId)[keyof typeof WalletTypeId];

export const PayoutTypeId = {
  SepaCreditTransfer: 1,
  SepaDirectDebit: 2,
  SepaInstantCreditTransfer: 3,
  InternationalTransfer: 4,
} as const;
export type PayoutTypeId = (typeof PayoutTypeId)[keyof typeof PayoutTypeId];

export const DocumentTypeId = {
  PoliceRecord: 2,
  Kbis: 4,
  Cv: 6,
  SwornStatement: 7,
  TurnoverForecast: 8,
  IdentityCard: 9,
  BankIdentityStatement: 11,
  ProofOfAddress: 12,
  MobilePhoneInvoice: 13,
  Invoice: 14,
  ResidencePermit: 15,
  DrivingLicence: 16,
  Passport: 17,
  Proxy: 18,
  CompanyRegistration: 19,
  TaxCertificate: 20,
  Payslip: 21,
  BankStatement: 22,
  LegalStatus: 23,
  TaxStatement: 24,
  ExemptionStatement: 25,
  LivenessResult: 26,
  HealthInsuranceCard: 27,
  Siren: 28,
  BeneficialOwnersList: 29,
  PepAssetsDeclaration: 31,
  ProfessionalRevenueDeclaration: 32,
  NonProfitOrgFinancialDeclaration: 33,
  CertifiedLivenessResult: 34,
  NaturalPersonRevenueAssetsDeclaration: 35,
  RealEstateProofOfOwnership: 36,
  ProofOfSecuritiesExchangeListing: 37,
  IdentityViaAuthorizedThirdParty: 38,
  FinancialStatement: 39,
  PublicAccountantStatement: 40,
  FamilyRecordBook: 41,
  BirthCertificate: 42,
  PowerOfAttorney: 43,
  OrganizationalChartViaAuthorizedThirdParty: 44,
  CompanyReportViaIndependentParty: 45,
  DeclarationOfActivityAndRevenues: 46,
  QualifiedESignatureCertificate: 47,
  QualifiedESignatureResult: 48,
  VideoConferenceResult: 49,
  CapitalizationTable: 50,
  W9Form: 51,
} as const;
export type DocumentTypeId = (typeof DocumentTypeId)[keyof typeof DocumentTypeId];

export const UserCodeStatus = {
  CanceledByEndUser: "110005",
  CanceledByOperator: "110006",
  Validated: "110009",
  InternalChecksInProgress: "110012",
} as const;

export const KycCodeStatus = {
  None: "110001",
  Pending: "110002",
  Incomplete: "110003",
  Refused: "110004",
  ReviewNeeded: "110005",
  Validated: "110006",
  Canceled: "110007",
} as const;

export const WalletCodeStatus = {
  ChecksInProgress: 120001,
  KycInProgress: 120002,
  ClosedByOperator: 120003,
  ClosedByEndUser: 120004,
  ReadyForUse: 120005,
} as const;

export const PayinCodeStatus = {
  PendingPayment: "140001",
  CanceledOrCannotBeValidated: "140004",
  AcceptedOrProcessed: "140005",
  PendingNonRepudiation: "140006",
  FraudSuspicionOrAmlCft: "140007",
  SepaTransferRefused: "140010",
} as const;

export const PayoutCodeStatus = {
  RefundRequestPopulated: "160001",
  RefundRequestReceivedByMail: "160002",
  InternallyValidated: "160003",
  TransferRequestSentToBank: "160004",
  RefundTransferValidated: "160005",
  CanceledByUser: "160006",
  BankAccountErrorOnFirstTransferOrDebit: "160007",
  FraudSuspicionOrAmlCft: "160008",
  CanceledByOperator: "160009",
  InsufficientAuthorizedBalance: "160010",
  PendingNonFatfCountry: "160011",
  PendingAmountExceeding10000Eur: "160012",
  PendingPoorScoring: "160013",
  SentPendingAcknowledgment: "160014",
  SuspendedByOperator: "160015",
} as const;

export const TransferCodeStatus = {
  Initiated: "150001",
  FraudSuspicion: "150002",
  CanceledByEndUser: "150003",
  CanceledByOperator: "150004",
  Validated: "150005",
  CanceledDueToLackOfFunds: "150006",
} as const;

export const PayoutRefundCodeStatus = {
  RefundMessageSentToScheme: "790001",
  CanceledByScheme: "790002",
  Validated: "790003",
  PendingNoResponseInLegalDelay: "790004",
} as const;

export const DocumentCodeStatus = {
  InitialBeforeUpload: "600001",
  SuspectedFraudDoNotShareWithEndUser: "600002",
  SuccessfullyUploaded: "600003",
  CancelledByUser: "600004",
  CancelledByOperator: "600005",
  FailedToWriteIntoContainer: "600006",
  FailedToReadFromContainer: "600007",
  Refused: "600008",
  Validated: "600009",
  OutOfScope: "600010",
  Expired: "600011",
  Falsified: "600012",
  Illegible: "600013",
  TruncatedDocument: "600014",
  Truncated: "600015",
  Incoherent: "600016",
  Other: "600017",
} as const;

export const ErrorsSchema = z.object({
  errors: z.array(
    z.object({
      type: z.enum([
        "invalid_client",
        "invalid_grant",
        "invalid_request",
        "unexpected_internal_server_error",
        "unsupported_grant_type",
      ]),
      code: z.enum([
        "authentication_error",
        "authorization_error",
        "functional_error",
        "input_validation_error",
        "invalid_parameter_in_request",
        "invalid_user_id",
        "resource_not_found_error",
        "undefined_cursor_error",
        "unexpected_error",
        "0",
      ]),
      message: z.string(),
      docUrl: z.url(),
      requestId: z.string(),
    }),
  ),
});
export type Errors = z.infer<typeof ErrorsSchema>;

export const TreezorCountryCodeSchema = z.enum(["BE", "DE", "ES", "FR", "IT", "LU", "NL"]);
export type TreezorCountryCode = z.infer<typeof TreezorCountryCodeSchema>;

export const UserSchema = z.object({
  userId: z.coerce.number().int(),
  userStatus: z.enum(["CANCELED", "PENDING", "VALIDATED"]),
  userTypeId: z.coerce
    .number()
    .pipe(
      z.union([
        z.literal(UserTypeId.Physical),
        z.literal(UserTypeId.Business),
        z.literal(UserTypeId.NonGovernmentalOrganization),
        z.literal(UserTypeId.GovernmentalOrganization),
      ]),
    ),
  userTag: z
    .union([
      z.templateLiteral(["projectId-", z.string()]),
      z.templateLiteral(["uid-", z.string()]),
      z.templateLiteral(["projectId-", z.string(), "-uid-", z.string()]),
      z.templateLiteral(["projectId-", z.string(), "-uid-", z.string(), "-pep"]),
      z.templateLiteral(["uid-", z.string(), "-pep"]),
      z.string(),
    ])
    .nullable(),
  parentUserId: z.coerce.number().int(),
  parentType: z.enum(["", "employee", "leader", "shareholder"]).nullable(),
  controllingPersonType: z.coerce.number().pipe(
    z.union([
      z.literal(0), // None
      z.literal(1), // Shareholder
      z.literal(2), // Other relationship
      z.literal(3), // Director
    ]),
  ),
  employeeType: z.coerce.number().pipe(
    z.union([
      z.literal(0), // None
      z.literal(1), // Leader
      z.literal(2), // Employee
    ]),
  ),
  specifiedUSPerson: z.coerce.number().pipe(
    z.union([
      z.literal(0), // Not a US person
      z.literal(1), // US person
    ]),
  ),
  title: z.enum(["", "M", "MLLE", "MME", "MX"]),
  firstname: z.string(),
  lastname: z.string(),
  middleNames: z.string(),
  birthday: z.preprocess(
    (value) => (value === "0000-00-00" || value === "" ? null : value),
    z.iso.date().nullable(),
  ),
  email: z.string(),
  address1: z.string(),
  address2: z.string(),
  address3: z.string().nullable(),
  postcode: z.string(),
  city: z.string(),
  state: z.string(),
  country: z.string(),
  countryName: z.string().nullable(),
  distributionCountry: TreezorCountryCodeSchema.nullable().optional(),
  secondaryAddress1: z.string().nullable(),
  secondaryAddress2: z.string().nullable(),
  secondaryAddress3: z.string().nullable(),
  secondaryPostcode: z.string().nullable(),
  secondaryCity: z.string().nullable(),
  secondaryState: z.string().nullable(),
  secondaryCountry: z.string().nullable(),
  clientId: z.string(),
  phone: z.string(),
  mobile: z.string(),
  nationality: z.string(),
  nationalityOther: z.string(),
  placeOfBirth: z.string(),
  birthCountry: z.string(),
  occupation: z.string(),
  legalName: z.string(),
  legalNameEmbossed: z.string(),
  legalRegistrationNumber: z.string(),
  legalTvaNumber: z.string(),
  legalRegistrationDate: z.preprocess(
    (value) => (value === "0000-00-00" || value === "" ? null : value),
    z.iso.date().nullable(),
  ),
  legalForm: z.string(),
  legalShareCapital: z.coerce.number().int(),
  legalSector: z.string(),
  legalAnnualTurnOver: z.enum([
    "",
    "0-39",
    "40-99",
    "100-249",
    "250-999",
    "1000-2999",
    "3000-9999",
    "10000-99999",
    "100000-*",
  ]),
  legalNetIncomeRange: z.enum([
    "",
    "*--500",
    "-499--150",
    "-149--50",
    "-49--10",
    "-9--1",
    "0-4",
    "5-9",
    "10-49",
    "50-149",
    "150-499",
    "500-*",
  ]),
  legalNumberOfEmployeeRange: z.enum(["", "0", "1-9", "10-99", "100-249", "250-*"]),
  effectiveBeneficiary: z.coerce.number().int(),
  isFrozen: z.preprocess(
    (value) => (value === "" ? null : value),
    z.coerce
      .number()
      .pipe(
        z.union([
          z.literal(0), // Not frozen
          z.literal(1), // Frozen
        ]),
      )
      .nullable()
      .optional(),
  ),
  language: z.string().nullable(),
  optInMailing: z.coerce.number().int().nullable().optional(),
  sepaCreditorIdentifier: z.string().nullable(),
  position: z.string(),
  activityOutsideEu: z.preprocess(
    (value) => (value === "" || value === null || value === undefined ? null : value),
    z.union([
      z.null(),
      z.coerce.number().pipe(
        z.union([
          z.literal(0), // No commercial activity outside the EU
          z.literal(1), // Commercial activity outside the EU
        ]),
      ),
    ]),
  ),
  economicSanctions: z.preprocess(
    (value) => (value === "" || value === null || value === undefined ? null : value),
    z.union([
      z.null(),
      z.coerce.number().pipe(
        z.union([
          z.literal(0), // Not subject to economic sanctions
          z.literal(1), // Subject to economic sanctions
        ]),
      ),
    ]),
  ),
  residentCountriesSanctions: z.preprocess(
    (value) => (value === "" || value === null || value === undefined ? null : value),
    z.union([
      z.null(),
      z.coerce.number().pipe(
        z.union([
          z.literal(0), // Not located in a comprehensively sanctioned country or territory
          z.literal(1), // Located in a comprehensively sanctioned country or territory
        ]),
      ),
    ]),
  ),
  involvedSanctions: z.preprocess(
    (value) => (value === "" || value === null || value === undefined ? null : value),
    z.union([
      z.null(),
      z.coerce.number().pipe(
        z.union([
          z.literal(0), // No sanctioned-country or sanctioned-target involvement
          z.literal(1), // Sanctioned-country or sanctioned-target involvement
        ]),
      ),
    ]),
  ),
  entitySanctionsQuestionnaire: z.preprocess(
    (value) => (value === "" || value === null || value === undefined ? undefined : value),
    z.union([
      z.undefined(),
      z.coerce.number().pipe(
        z.union([
          z.literal(0), // Not applicable
          z.literal(1), // Only the legal entity
          z.literal(2), // Legal entity and all subsidiaries held at 100%
          z.literal(3), // Legal entity and all subsidiaries held at 50% or more
          z.literal(4), // Legal entity and a list of subsidiaries
        ]),
      ),
    ]),
  ),
  sanctionsQuestionnaireDate: z.string().nullable(),
  timezone: z.string().nullable(),
  sourceOfFunds: z
    .enum([
      "",
      "donation",
      "inheritance",
      "loan",
      "lottery",
      "pension",
      "proceeds_from_investment",
      "proceeds_from_sale",
      "salary",
      "savings",
    ])
    .nullable(),
  legalSectorType: z.enum(["", "NACE", "NAF"]).nullable(),
  isOnStockExchange: z.coerce.number().pipe(
    z.union([
      z.literal(0), // Not listed on a stock exchange
      z.literal(1), // Listed on a stock exchange
    ]),
  ),
  codeStatus: z.enum([
    UserCodeStatus.CanceledByEndUser,
    UserCodeStatus.CanceledByOperator,
    UserCodeStatus.Validated,
    UserCodeStatus.InternalChecksInProgress,
  ]),
  informationStatus: z.string(),
  createdDate: z.string(),
  modifiedDate: z.string(),
  walletCount: z.coerce.number().int(),
  payinCount: z.coerce.number().int(),
  occupationCategory: z.preprocess(
    (value) => (value === "" ? null : value),
    z.coerce
      .number()
      .nullable()
      .optional()
      .pipe(
        z.union([
          z.literal(1), // Pupil, student, or apprentice
          z.literal(2), // Beneficiary of unemployment assistance
          z.literal(3), // Without professional activity
          z.literal(4), // Executive and intellectual profession in the private sector
          z.literal(5), // Employee in the private sector
          z.literal(6), // Business owner
          z.literal(7), // Executive and intellectual profession in the public sector
          z.literal(8), // Employee and officer in the public sector
          z.literal(9), // Farmer
          z.literal(10), // Self-employed
          z.literal(11), // Craftsman
          z.literal(12), // Shopkeeper and related professions
          z.literal(13), // Freelance profession
          z.literal(14), // Medical freelance profession or medical practitioner
          z.literal(15), // Retired
        ]),
      )
      .nullable()
      .optional(),
  ),
  personalAssetsRange: z.preprocess(
    (value) => (value === "" ? null : value),
    z.coerce
      .number()
      .nullable()
      .optional()
      .pipe(
        z.union([
          z.literal(0), // None
          z.literal(1), // EUR 0 to 15,000
          z.literal(2), // EUR 15,001 to 50,000
          z.literal(3), // EUR 50,001 to 150,000
          z.literal(4), // EUR 150,001 to 350,000
          z.literal(5), // EUR 350,001 to 750,000
          z.literal(6), // EUR 750,001 to 1,500,000
          z.literal(7), // More than EUR 1,500,000
        ]),
      )
      .nullable()
      .optional(),
  ),
  monthlyIncomeRange: z.preprocess(
    (value) => (value === "" ? null : value),
    z.coerce
      .number()
      .nullable()
      .optional()
      .pipe(
        z.union([
          z.literal(1), // EUR 0 to 999 per month
          z.literal(2), // EUR 1,000 to 1,800 per month
          z.literal(3), // EUR 1,801 to 3,500 per month
          z.literal(4), // EUR 3,501 to 5,000 per month
          z.literal(5), // EUR 5,001 to 6,500 per month
          z.literal(6), // EUR 6,501 to 10,000 per month
          z.literal(7), // More than EUR 10,000 per month
        ]),
      )
      .nullable()
      .optional(),
  ),
  birthCityCode: z.string().nullable().optional(),
});
export const UserLooseSchema = z.looseObject(UserSchema.shape);
export type User = z.infer<typeof UserSchema>;

export const KycSchema = z.object({
  userId: z.coerce.number().int(),
  clientId: z.coerce.number().int(),
  distributionCountry: TreezorCountryCodeSchema.nullable(),
  kycReviewComment: z.string().nullable(),
  kycStatus: z.enum([
    "NONE",
    "PENDING",
    "INCOMPLETE",
    "REFUSED",
    "REVIEW NEEDED",
    "VALIDATED",
    "CANCELED",
  ]),
  kycReviewType: z.enum(["ONBOARDING", "PERIODIC", "ON_EVENT"]),
  codeStatus: z.enum([
    KycCodeStatus.None,
    KycCodeStatus.Pending,
    KycCodeStatus.Incomplete,
    KycCodeStatus.Refused,
    KycCodeStatus.ReviewNeeded,
    KycCodeStatus.Validated,
    KycCodeStatus.Canceled,
  ]),
  informationStatus: z.string(),
  lastRequestDatetime: z.iso.datetime().nullable(),
  lastReviewDatetime: z.iso.datetime().nullable(),
  nextReviewDate: z.iso.date().nullable(),
});
export const KycLooseSchema = z.looseObject(KycSchema.shape);
export type Kyc = z.infer<typeof KycSchema>;

export type UserWithKyc = User & { kyc: Kyc | undefined };

export const RelationshipSchema = z.object({
  relationshipId: z.coerce.number().int(),
  targetUserId: z.coerce.number().int(),
  sourceUserId: z.coerce.number().int(),
  relationshipTypeId: z.coerce
    .number()
    .pipe(
      z.union([
        z.literal(RelationshipTypeId.LegalRepresentative),
        z.literal(RelationshipTypeId.EffectiveBeneficiaryShares),
        z.literal(RelationshipTypeId.CompanyRepresentative),
        z.literal(RelationshipTypeId.Employee),
      ]),
    ),
  relationshipTypeName: z.enum([
    "LEGAL_REPRESENTATIVE",
    "EFFECTIVE_BENEFICIARY_SHARES",
    "COMPANY_REPRESENTATIVE",
    "EMPLOYEE",
  ]),
  value: z.string().nullable(),
});
export const RelationshipLooseSchema = z.looseObject(RelationshipSchema.shape);
export type Relationship = z.infer<typeof RelationshipSchema>;

export const TaxResidenceSchema = z.object({
  id: z.coerce.number().int(),
  userId: z.coerce.number().int(),
  country: z.string(),
  taxPayerId: z.string().nullable(),
  liabilityWaiver: z.coerce.boolean(),
  createdDate: z.string(),
  lastUpdate: z.string(),
  deletedDate: z.string(),
  isDeleted: z.coerce.boolean(),
});
export const TaxResidenceLooseSchema = z.looseObject(TaxResidenceSchema.shape);
export type TaxResidence = z.infer<typeof TaxResidenceSchema>;

export const WalletSchema = z.object({
  walletId: z.coerce.number().int(),
  walletTypeId: z.coerce
    .number()
    .pipe(
      z.union([
        z.literal(WalletTypeId.ElectronicMoney),
        z.literal(WalletTypeId.PaymentAccountSepa),
        z.literal(WalletTypeId.Master),
      ]),
    ),
  walletStatus: z.enum(["CANCELED", "PENDING", "VALIDATED"]),
  codeStatus: z.coerce
    .number()
    .pipe(
      z.union([
        z.literal(WalletCodeStatus.ChecksInProgress),
        z.literal(WalletCodeStatus.KycInProgress),
        z.literal(WalletCodeStatus.ClosedByOperator),
        z.literal(WalletCodeStatus.ClosedByEndUser),
        z.literal(WalletCodeStatus.ReadyForUse),
      ]),
    ),
  informationStatus: z.string().nullable(),
  walletTag: z.union([z.templateLiteral(["projectId-", z.string()]), z.string()]).nullable(),
  userId: z.coerce.number().int(),
  userLastname: z.string(),
  userFirstname: z.string(),
  tariffId: z.coerce.number().int(),
  eventName: z.string(),
  eventMessage: z.string().nullable(),
  bic: z.enum(["TRZOFR21XXX", "TRZODEB2XXX", "TRZOITM2XXX", "TRZOESM2XXX"]),
  iban: z.string(),
  currency: z.string(),
  createdDate: z.string(),
  modifiedDate: z.string(),
  solde: z.coerce.number(),
  authorizedBalance: z.coerce.number(),
  country: z.string(),
});
export const WalletLooseSchema = z.looseObject(WalletSchema.shape);
export type Wallet = z.infer<typeof WalletSchema>;

export const BalanceSchema = z.object({
  walletId: z.coerce.number().int(),
  currentBalance: z.coerce.number(),
  authorizations: z.coerce.number(),
  authorizedBalance: z.coerce.number(),
  currency: z.string(),
  calculationDate: z.string(),
});
export const BalanceLooseSchema = z.looseObject(BalanceSchema.shape);
export type Balance = z.infer<typeof BalanceSchema>;

export const SCAWalletSchema = z.object({
  id: z.string(),
  status: z.enum(["ACTIVE", "CREATED", "CREATING", "DELETED", "INITIALIZING"]).nullable(),
  subStatus: z
    .enum([
      "",
      "CREATING_IN_PROGRESS",
      "CREATED_BLOCKED",
      "CREATED_READY",
      "INITIALIZING_MOBILE",
      "ACTIVATED_LOGGED_IN",
      "ACTIVATED_LOGGED_OUT",
      "DELETED_BY_ISSUER",
      "DELETED_UNINSTALLED",
    ])
    .nullable(),
  passcodeStatus: z.enum(["NONE", "NOT_SET", "SET", "TO_BE_CHANGED"]).nullable(),
  locked: z.boolean().nullable(),
  lockReasons: z.array(
    z.enum([
      "FRAUDULENT_USE_SUSPECTED_BY_CLIENT",
      "FRAUDULENT_USE_SUSPECTED_BY_ISSUER",
      "ISSUER",
      "LOST_DEVICE",
      "STOLEN_DEVICE",
      "TERMINATE_SERVICE",
    ]),
  ),
  lockMessage: z.string().nullable(),
  settingsProfile: z.enum(["default", "webauthn"]).nullable(),
  activationCode: z.string().nullable(),
  creationDate: z.string().nullable(),
  deletionDate: z.string().nullable(),
  activationCodeExpiryDate: z.string().nullable(),
  authenticationMethods: z.array(
    z.union([
      z.object({
        aaguid: z.string(),
        attestationType: z.string(),
        backupEligible: z.boolean(),
        backupStatus: z.boolean(),
        counter: z.coerce.number().int(),
        credentialPublicKey: z.string(),
        publicKeyCredentialId: z.string(),
        transports: z.array(z.enum(["ble", "hybrid", "internal", "nfc", "usb"])),
        type: z.string(),
        userHandle: z.string(),
        uvInitialized: z.boolean(),
      }),
    ]),
  ),
  invalidActivationAttempts: z.string().nullable(),
  userId: z.string(),
  scaWalletTag: z.union([z.templateLiteral(["uid-", z.string()]), z.string()]).nullable(),
  clientId: z.string().optional(),
  activationDate: z.string().nullable(),
  lockDate: z.string().nullable(),
  unlockDate: z.string().nullable(),
  resetPinDate: z.string().nullable(),
});
export const SCAWalletLooseSchema = z.looseObject({
  ...SCAWalletSchema.shape,
  authenticationMethods: z.array(
    z.union([z.looseObject(SCAWalletSchema.shape.authenticationMethods.element.options[0].shape)]),
  ),
});
export type SCAWallet = z.infer<typeof SCAWalletSchema>;

const OperationMetadataSchema = z.discriminatedUnion("initialFlow", [
  z.object({
    initialFlow: z.literal("cardTransaction"),
    metadata: z.object({
      mcc: z.object({
        code: z.coerce.number().int(),
      }),
      mid: z.object({
        value: z.string(),
        name: z.string(),
        city: z.string().nullable(),
        country: z.string().nullable(),
        address: z.string().nullable(),
        paymentCountry: z.string().nullable(),
      }),
      localAmount: z.object({
        amount: z.coerce.number().int(), // Amount in cents
        currency: z.string(),
      }),
      authorisationResponseCode: z.object({
        action: z.string(),
        description: z.string(),
        value: z.coerce.number().int().nullable(),
      }),
      paymentLifeCycle: z.array(
        z.object({
          authorizationIssuerTime: z.string(),
          valueDate: z.string().nullable(),
          paymentAmount: z.string(),
          paymentLocalAmount: z.string(),
          paymentCurrency: z.string(),
        }),
      ),
      paymentCountry: z.string().nullable(),
      cardId: z.string(),
      userId: z.string(),
      paymentId: z.string(),
      embossedName: z.string(),
      publicToken: z.string(),
      maskedPan: z.string(),
      isPinValidated: z.boolean(),
      is3DS: z.string().nullable(),
      exemptionType3ds: z.string().nullable(),
      optimizedMerchantName: z.string().nullable(),
      merchantLogo: z.url().nullable(),
      merchantCategory: z
        .enum([
          "Bills, Fees & Services",
          "Cash & Banking",
          "Charity & Donations",
          "Cultural & Education",
          "Eating Out",
          "Entertainment & Recreation",
          "Groceries & Supermarkets",
          "Health & Wellness",
          "Home & Family",
          "Other",
          "Retail & Shopping",
          "Transportation & Mobility",
          "Travel & Holidays",
        ])
        .nullable(),
      merchantCategoryGroup: z.string().nullable(),
      merchantCategoryType: z.string().nullable(),
      transactionSubtype: z.string().nullable(),
      authorizationNote: z.string(),
    }),
  }),
  z.object({
    initialFlow: z.literal("chargeback"),
    metadata: z.object({
      payinId: z.string(),
      payinRefundId: z.string(),
      transactionReference: z.string(),
      payinTag: z.string().nullable(),
      additionalData: z.array(z.string()),
      chargebackReason: z.string(),
    }),
  }),
  z.object({
    initialFlow: z.literal("payin"),
    metadata: z.object({
      messageToUser: z.string().nullable(),
      codeStatus: z.enum([
        PayinCodeStatus.PendingPayment,
        PayinCodeStatus.CanceledOrCannotBeValidated,
        PayinCodeStatus.AcceptedOrProcessed,
        PayinCodeStatus.PendingNonRepudiation,
        PayinCodeStatus.FraudSuspicionOrAmlCft,
        PayinCodeStatus.SepaTransferRefused,
      ]),
      informationStatus: z.string().nullable(),
      paymentMethodId: z.union([
        z.literal(20), // Received SEPA Credit Transfer
        z.literal(21), // Emitted SEPA Core Direct Debit
        z.literal(22), // Emitted SEPA B2B Direct Debit
        z.literal(23), // Deprecated Full Hosted HTML Payment Form
        z.literal(24), // Deprecated IFrame Payment Form
        z.literal(25), // Card topups
        z.literal(26), // Check cashing
        z.literal(27), // Received SEPA Instant Credit Transfer
        z.literal(28), // Received International Transfer
      ]),
      payinTag: z.string().nullable(),
      additionalData: z.array(z.string()).nullable(),
      ibanFullname: z.string().nullable(),
      ibanBic: z.string().nullable(),
      dbtrIBAN: z.string().nullable(),
      mandateId: z.string().nullable().optional(),
      ibanTxEndToEndId: z.string().nullable(),
      payinId: z.string(),
      debtorName: z.string().nullable(),
      uniqueMandateReference: z.string().nullable().optional(),
    }),
  }),
  z.object({
    initialFlow: z.literal("payinRefund"),
    metadata: z.object({
      payinId: z.string().nullable(),
      codeStatus: z.string().nullable(),
      informationStatus: z.string().nullable(),
      paymentMethodId: z.union([
        z.literal(20), // Received SEPA Credit Transfer
        z.literal(21), // Emitted SEPA Core Direct Debit
        z.literal(22), // Emitted SEPA B2B Direct Debit
        z.literal(23), // Deprecated Full Hosted HTML Payment Form
        z.literal(24), // Deprecated IFrame Payment Form
        z.literal(25), // Card topups
        z.literal(26), // Check cashing
        z.literal(27), // Received SEPA Instant Credit Transfer
        z.literal(28), // Received International Transfer
      ]),
      reasonTms: z.string().nullable(),
      payinTag: z.string().nullable(),
      messageToUser: z.string().nullable(),
      ibanFullName: z.string().nullable(),
      ibanBic: z.string().nullable(),
      dbtrIBAN: z.string().nullable(),
      ibanTxEndToEndId: z.string().nullable(),
      mandateId: z.string().nullable().optional(),
      debtorName: z.string().nullable(),
      uniqueMandateReference: z.string().nullable().optional(),
      additionalData: z.array(z.string()),
    }),
  }),
  z.object({
    initialFlow: z.literal("payout"),
    metadata: z.object({
      payoutTag: z.string().nullable(),
      beneficiaryId: z.string(),
      label: z.string().nullable(),
      codeStatus: z.enum([
        PayoutCodeStatus.RefundRequestPopulated,
        PayoutCodeStatus.RefundRequestReceivedByMail,
        PayoutCodeStatus.InternallyValidated,
        PayoutCodeStatus.TransferRequestSentToBank,
        PayoutCodeStatus.RefundTransferValidated,
        PayoutCodeStatus.CanceledByUser,
        PayoutCodeStatus.BankAccountErrorOnFirstTransferOrDebit,
        PayoutCodeStatus.FraudSuspicionOrAmlCft,
        PayoutCodeStatus.CanceledByOperator,
        PayoutCodeStatus.InsufficientAuthorizedBalance,
        PayoutCodeStatus.PendingNonFatfCountry,
        PayoutCodeStatus.PendingAmountExceeding10000Eur,
        PayoutCodeStatus.PendingPoorScoring,
        PayoutCodeStatus.SentPendingAcknowledgment,
        PayoutCodeStatus.SuspendedByOperator,
      ]),
      informationStatus: z.string().nullable(),
      payoutTypeId: z
        .union([
          z.literal(PayoutTypeId.SepaCreditTransfer),
          z.literal(PayoutTypeId.SepaDirectDebit),
          z.literal(PayoutTypeId.SepaInstantCreditTransfer),
          z.literal(PayoutTypeId.InternationalTransfer),
        ])
        .nullable(),
      uniqueMandateReference: z.string().nullable().optional(),
      supportingFileLink: z.string().nullable(),
      endToEndId: z.string().nullable(),
      reasonCode: z.string().nullable(),
      reasonDescription: z.string().nullable(),
      beneficiaryName: z.string().nullable(),
    }),
  }),
  z.object({
    initialFlow: z.literal("payoutRefund"),
    metadata: z.object({
      payoutRefundTag: z.string().nullable(),
      codeStatus: z
        .enum([
          PayoutRefundCodeStatus.RefundMessageSentToScheme,
          PayoutRefundCodeStatus.CanceledByScheme,
          PayoutRefundCodeStatus.Validated,
          PayoutRefundCodeStatus.PendingNoResponseInLegalDelay,
        ])
        .nullable(),
      informationStatus: z.string().nullable(),
      payoutTypeId: z.union([
        z.literal(PayoutTypeId.SepaCreditTransfer),
        z.literal(PayoutTypeId.SepaDirectDebit),
        z.literal(PayoutTypeId.SepaInstantCreditTransfer),
        z.literal(PayoutTypeId.InternationalTransfer),
      ]),
      requestComment: z.string().nullable(),
      reasonCode: z.string().nullable(),
      refundDate: z.string().nullable(),
      refundComment: z.string().nullable(),
      payoutId: z.string().nullable(),
      payoutTag: z.string().nullable(),
      beneficiaryId: z.string().nullable(),
      beneficiaryName: z.string().nullable(),
      label: z.string().nullable(),
      endToEndId: z.string().nullable(),
      uniqueMandateReference: z.string().nullable().optional(),
    }),
  }),
  z.object({
    initialFlow: z.literal("transfer"),
    metadata: z.object({
      label: z.string().nullable(),
      codeStatus: z.enum([
        TransferCodeStatus.Initiated,
        TransferCodeStatus.FraudSuspicion,
        TransferCodeStatus.CanceledByEndUser,
        TransferCodeStatus.CanceledByOperator,
        TransferCodeStatus.Validated,
        TransferCodeStatus.CanceledDueToLackOfFunds,
      ]),
      tag: z.string().nullable(),
      informationStatus: z.string().nullable(),
      creditorWalletId: z.string(),
      creditorFirstName: z.string(),
      creditorLastName: z.string(),
      creditorLegalName: z.string(),
      creditorWalletEventName: z.string(),
      debtorWalletId: z.string(),
      debtorFirstName: z.string(),
      debtorLastName: z.string(),
      debtorLegalName: z.string(),
      debtorWalletEventName: z.string(),
    }),
  }),
]);
const OperationMetadataLooseSchema = z.discriminatedUnion("initialFlow", [
  z.looseObject({
    initialFlow: z.literal("cardTransaction"),
    metadata: z.looseObject({
      ...OperationMetadataSchema.options[0].shape.metadata.shape,
      mcc: z.looseObject(OperationMetadataSchema.options[0].shape.metadata.shape.mcc.shape),
      mid: z.looseObject(OperationMetadataSchema.options[0].shape.metadata.shape.mid.shape),
      localAmount: z.looseObject(
        OperationMetadataSchema.options[0].shape.metadata.shape.localAmount.shape,
      ),
      authorisationResponseCode: z.looseObject(
        OperationMetadataSchema.options[0].shape.metadata.shape.authorisationResponseCode.shape,
      ),
      paymentLifeCycle: z.array(
        z.looseObject(
          OperationMetadataSchema.options[0].shape.metadata.shape.paymentLifeCycle.element.shape,
        ),
      ),
    }),
  }),
  z.looseObject({
    initialFlow: z.literal("chargeback"),
    metadata: z.looseObject(OperationMetadataSchema.options[1].shape.metadata.shape),
  }),
  z.looseObject({
    initialFlow: z.literal("payin"),
    metadata: z.looseObject(OperationMetadataSchema.options[2].shape.metadata.shape),
  }),
  z.looseObject({
    initialFlow: z.literal("payinRefund"),
    metadata: z.looseObject(OperationMetadataSchema.options[3].shape.metadata.shape),
  }),
  z.looseObject({
    initialFlow: z.literal("payout"),
    metadata: z.looseObject(OperationMetadataSchema.options[4].shape.metadata.shape),
  }),
  z.looseObject({
    initialFlow: z.literal("payoutRefund"),
    metadata: z.looseObject(OperationMetadataSchema.options[5].shape.metadata.shape),
  }),
  z.looseObject({
    initialFlow: z.literal("transfer"),
    metadata: z.looseObject(OperationMetadataSchema.options[6].shape.metadata.shape),
  }),
]);
const OperationBaseSchema = z.object({
  amount: z.object({
    amount: z.coerce.number().int(), // 10.00 -> 1000
    currency: z.string(),
  }),
  date: z.object({
    creation: z.iso.datetime({ offset: true }),
    settlement: z.iso.datetime({ offset: true }).nullable(),
  }),
  direction: z.enum(["CREDIT", "DEBIT"]),
  label: z.string().nullable(),
  objectId: z.string(),
  transactionId: z.string().nullable().optional(), // null when status is not SETTLED
  operationType: z.enum([
    "bankDirectDebit",
    "bankTransfer",
    "cardTopup",
    "cardTransaction",
    "check",
    "creditNote",
    "fees",
    "intBankTransfer",
    "instantBankTransfer",
    "other",
    "payinRefund",
    "payoutRefund",
    "transferRefund",
    "walletTransfer",
  ]),
  status: z.enum(["AUTHORIZED", "CANCELED", "DECLINED", "SETTLED"]),
  walletId: z.coerce.number().int(),
});
export const OperationSchema = OperationBaseSchema.and(OperationMetadataSchema);
export const OperationLooseSchema = z
  .looseObject({
    ...OperationBaseSchema.shape,
    amount: z.looseObject(OperationBaseSchema.shape.amount.shape),
    date: z.looseObject(OperationBaseSchema.shape.date.shape),
  })
  .and(OperationMetadataLooseSchema);
export type Operation = z.infer<typeof OperationSchema> & {
  categoryId?: CategoryId;
  trackedCategorySplits?: TrackedCategorySplit[];
  /** AI suggestion for a tracked category; present means pending user accept/reject. */
  suggestedTrackedCategoryId?: string;
  /** Last AI suggestion the user rejected for this transaction. */
  rejectedSuggestedTrackedCategoryId?: string;
  is_flagged?: boolean;
  transferRequest?: TransferRequest & { id: string };
  files?: TransactionFile[];
};

export const BeneficiarySchema = z.object({
  id: z.coerce.number().int(),
  tag: z.union([z.templateLiteral(["projectId-", z.string()]), z.string()]).nullable(),
  userId: z.coerce.number().int(),
  nickName: z.string(),
  name: z.string(),
  address: z.string(),
  // Treezor's top-level iban is masked; the real IBAN is metadata.iban
  bic: z.string(),
  sepaCreditorIdentifier: z.string(),
  sddB2bWhitelist: z.array(
    z.object({
      uniqueMandateReference: z.string(),
      isRecurrent: z.boolean(),
      walletId: z.coerce.number().int(),
    }),
  ),
  sddCoreBlacklist: z.array(z.string()),
  usableForSct: z.boolean(),
  sddCoreKnownUniqueMandateReference: z.array(z.string()),
  isActive: z.boolean(),
  createdDate: z.string(),
  modifiedDate: z.string(),
  metadata: z
    .object({
      iban: z.string().optional(),
    })
    .optional(),
});
export const BeneficiaryLooseSchema = z.looseObject({
  ...BeneficiarySchema.shape,
  sddB2bWhitelist: z.array(z.looseObject(BeneficiarySchema.shape.sddB2bWhitelist.element.shape)),
  metadata: z.looseObject(BeneficiarySchema.shape.metadata.unwrap().shape).optional(),
});
export type Beneficiary = z.infer<typeof BeneficiarySchema>;

export const BeneficiaryValidationSchema = z.object({
  beneficiaryValidationId: z.string(),
  createdDate: z.iso.datetime(),
  vop: z.object({
    matchedName: z.string().nullable(),
    reasonCode: z
      .enum([
        "9001", // The bank or payment service provider is not reachable.
        "9007", // The account does not exist.
        "9008", // The account is closed.
        "9009", // Technical error.
        "9010", // Other reason.
      ])
      .nullable()
      .optional(),
    recurrenceType: z.enum(["default", "scheduled"]),
    result: z.enum([
      "MTCH", // Match
      "NMTC", // No Match
      "CMTC", // Close Match
      "NOAP", // Verification Not Possible / Verification Unavailable
    ]),
  }),
});
export const BeneficiaryValidationLooseSchema = z.looseObject({
  ...BeneficiaryValidationSchema.shape,
  vop: z.looseObject(BeneficiaryValidationSchema.shape.vop.shape),
});
export type BeneficiaryValidation = z.infer<typeof BeneficiaryValidationSchema>;

export const PayoutSchema = z.object({
  payoutId: z.coerce.number().int(),
  payoutTag: z
    .union([
      z.templateLiteral(["projectId-", z.string()]),
      z.templateLiteral(["projectId-", z.string(), "-transferRequest-", z.string()]),
      z.string(),
    ])
    .nullable(),
  payoutStatus: z.enum(["CANCELED", "PENDING", "VALIDATED"]),
  payoutTypeId: z.coerce
    .number()
    .pipe(
      z.union([
        z.literal(PayoutTypeId.SepaCreditTransfer),
        z.literal(PayoutTypeId.SepaDirectDebit),
        z.literal(PayoutTypeId.SepaInstantCreditTransfer),
        z.literal(PayoutTypeId.InternationalTransfer),
      ]),
    ),
  payoutType: z.enum(["Credit Transfer", "Direct Debit", "Sepa Instant Credit Transfer"]),
  walletId: z.coerce.number().int(),
  payoutDate: z.preprocess(
    (value) => (value === "0000-00-00" || value === "" ? null : value),
    z.iso.date().nullable(),
  ),
  walletEventName: z.string(),
  userFirstname: z.string(),
  userLastname: z.string(),
  userId: z.coerce.number().int(),
  beneficiaryId: z.coerce.number().int(),
  uniqueMandateReference: z.string().nullable(),
  bankaccountIBAN: z.string().nullable(),
  label: z.string(),
  amount: z.string(), // decimal euros as string, not cents
  currency: z.string(),
  partnerFee: z.string(),
  createdDate: z.string(),
  modifiedDate: z.string(),
  virtualIbanId: z.coerce.number().int().nullable(),
  virtualIbanReference: z.string().nullable(),
  codeStatus: z.enum([
    PayoutCodeStatus.RefundRequestPopulated,
    PayoutCodeStatus.RefundRequestReceivedByMail,
    PayoutCodeStatus.InternallyValidated,
    PayoutCodeStatus.TransferRequestSentToBank,
    PayoutCodeStatus.RefundTransferValidated,
    PayoutCodeStatus.CanceledByUser,
    PayoutCodeStatus.BankAccountErrorOnFirstTransferOrDebit,
    PayoutCodeStatus.FraudSuspicionOrAmlCft,
    PayoutCodeStatus.CanceledByOperator,
    PayoutCodeStatus.InsufficientAuthorizedBalance,
    PayoutCodeStatus.PendingNonFatfCountry,
    PayoutCodeStatus.PendingAmountExceeding10000Eur,
    PayoutCodeStatus.PendingPoorScoring,
    PayoutCodeStatus.SentPendingAcknowledgment,
    PayoutCodeStatus.SuspendedByOperator,
  ]),
  informationStatus: z.string(),
  supportingFileLink: z.string(),
  endToEndId: z.string().nullable(),
  reasonCode: z.string().nullable(),
  reasonDescription: z.string().nullable(),
  internationalBeneficiaryId: z.string().nullable().optional(),
  quotationId: z.string().nullable().optional(),
  beneficiaryValidationId: z.string().nullable(),
  totalRows: z.coerce.number().int().nullable(),
  metadata: z.looseObject({}).nullable().optional(),
});
export const PayoutLooseSchema = z.looseObject(PayoutSchema.shape);
export type Payout = z.infer<typeof PayoutSchema>;

export type TransferRequest = {
  files?: TransactionFile[];
  amount: string;
  beneficiaryName: string;
  beneficiaryIban: string;
  label?: string;
  reference?: string;
  createdAt: Timestamp;
  status: "pending" | "completed" | "accepted" | "rejected" | "cancelled";
  statusUpdatedAt: Timestamp | undefined;
  trackedCategoryId?: string;
  decisions?: Record<string, TransferRequestDecision>;
  verificationOfPayee: VerificationOfPayee | undefined;

  treezorOperationId?: string;
};

export const DocumentTypeIdSchema = z.coerce
  .number()
  .pipe(
    z.union([
      z.literal(DocumentTypeId.PoliceRecord),
      z.literal(DocumentTypeId.Kbis),
      z.literal(DocumentTypeId.Cv),
      z.literal(DocumentTypeId.SwornStatement),
      z.literal(DocumentTypeId.TurnoverForecast),
      z.literal(DocumentTypeId.IdentityCard),
      z.literal(DocumentTypeId.BankIdentityStatement),
      z.literal(DocumentTypeId.ProofOfAddress),
      z.literal(DocumentTypeId.MobilePhoneInvoice),
      z.literal(DocumentTypeId.Invoice),
      z.literal(DocumentTypeId.ResidencePermit),
      z.literal(DocumentTypeId.DrivingLicence),
      z.literal(DocumentTypeId.Passport),
      z.literal(DocumentTypeId.Proxy),
      z.literal(DocumentTypeId.CompanyRegistration),
      z.literal(DocumentTypeId.TaxCertificate),
      z.literal(DocumentTypeId.Payslip),
      z.literal(DocumentTypeId.BankStatement),
      z.literal(DocumentTypeId.LegalStatus),
      z.literal(DocumentTypeId.TaxStatement),
      z.literal(DocumentTypeId.ExemptionStatement),
      z.literal(DocumentTypeId.LivenessResult),
      z.literal(DocumentTypeId.HealthInsuranceCard),
      z.literal(DocumentTypeId.Siren),
      z.literal(DocumentTypeId.BeneficialOwnersList),
      z.literal(DocumentTypeId.PepAssetsDeclaration),
      z.literal(DocumentTypeId.ProfessionalRevenueDeclaration),
      z.literal(DocumentTypeId.NonProfitOrgFinancialDeclaration),
      z.literal(DocumentTypeId.CertifiedLivenessResult),
      z.literal(DocumentTypeId.NaturalPersonRevenueAssetsDeclaration),
      z.literal(DocumentTypeId.RealEstateProofOfOwnership),
      z.literal(DocumentTypeId.ProofOfSecuritiesExchangeListing),
      z.literal(DocumentTypeId.IdentityViaAuthorizedThirdParty),
      z.literal(DocumentTypeId.FinancialStatement),
      z.literal(DocumentTypeId.PublicAccountantStatement),
      z.literal(DocumentTypeId.FamilyRecordBook),
      z.literal(DocumentTypeId.BirthCertificate),
      z.literal(DocumentTypeId.PowerOfAttorney),
      z.literal(DocumentTypeId.OrganizationalChartViaAuthorizedThirdParty),
      z.literal(DocumentTypeId.CompanyReportViaIndependentParty),
      z.literal(DocumentTypeId.DeclarationOfActivityAndRevenues),
      z.literal(DocumentTypeId.QualifiedESignatureCertificate),
      z.literal(DocumentTypeId.QualifiedESignatureResult),
      z.literal(DocumentTypeId.VideoConferenceResult),
      z.literal(DocumentTypeId.CapitalizationTable),
      z.literal(DocumentTypeId.W9Form),
    ]),
  );

export const PreReviewDocumentStatus = {
  Pending: 0,
  Refused: 1,
  Validated: 2,
} as const;

export const PreReviewDocumentStatusSchema = z.coerce
  .number()
  .pipe(
    z.union([
      z.literal(PreReviewDocumentStatus.Pending),
      z.literal(PreReviewDocumentStatus.Refused),
      z.literal(PreReviewDocumentStatus.Validated),
    ]),
  );
export type PreReviewDocumentStatus = z.infer<typeof PreReviewDocumentStatusSchema>;

export const PreReviewDocumentSchema = z.object({
  documentId: z.string(),
  documentType: DocumentTypeIdSchema,
  status: PreReviewDocumentStatusSchema,
  userId: z.coerce.number().int(),
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
  comment: z
    .string()
    .nullable()
    .transform((value) => value || null),
});
export type PreReviewDocument = z.infer<typeof PreReviewDocumentSchema>;

export const DocumentSchema = z.object({
  documentId: z.coerce.number().int(),
  documentTag: z.string().nullable(),
  clientId: z.coerce.number().int(),
  userId: z.coerce.number().int(),
  name: z.string(),
  documentStatus: z.enum(["PENDING", "CANCELED", "REFUSED", "VALIDATED"]),
  documentTypeId: DocumentTypeIdSchema,
  documentType: z.string(),
  fileName: z.string(),
  createdDate: z.string(),
  modifiedDate: z.string(), // 0000-00-00 00:00:00 if never modified
  codeStatus: z.enum([
    DocumentCodeStatus.InitialBeforeUpload,
    DocumentCodeStatus.SuspectedFraudDoNotShareWithEndUser,
    DocumentCodeStatus.SuccessfullyUploaded,
    DocumentCodeStatus.CancelledByUser,
    DocumentCodeStatus.CancelledByOperator,
    DocumentCodeStatus.FailedToWriteIntoContainer,
    DocumentCodeStatus.FailedToReadFromContainer,
    DocumentCodeStatus.Refused,
    DocumentCodeStatus.Validated,
    DocumentCodeStatus.OutOfScope,
    DocumentCodeStatus.Expired,
    DocumentCodeStatus.Falsified,
    DocumentCodeStatus.Illegible,
    DocumentCodeStatus.TruncatedDocument,
    DocumentCodeStatus.Truncated,
    DocumentCodeStatus.Incoherent,
    DocumentCodeStatus.Other,
  ]),
  informationStatus: z.string().nullable(),
  residenceId: z.coerce.number().int().nullable(),
});
export const DocumentLooseSchema = z.looseObject(DocumentSchema.shape);
